import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { getStationType } from 'utils/StationUtils'
import { greyBlue, mainBlue } from 'utils/constants/ColorTheme'
import i18n from 'simple-react-i18n'
import { nbPerPageLabel, nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import { intersectionWith, isUndefined, keyBy, keys } from 'lodash'
import { getLabel } from 'utils/StoreUtils'
import { normalizeString } from 'utils/StringUtil'
import SimpleSelectionTable, { ParameterFilterField } from 'components/datatable/SimpleSelectionTable'
import PropTypes from 'prop-types'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import { ButtonMUI } from 'components/styled/Buttons'
import StepperDialog from 'components/modal/StepperDialog'
import useListIndexed from 'utils/customHook/useListIndexed'
import Select from 'components/forms/Select'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { CardTable, NewTable } from 'components/datatable/NewTable'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import EditIcon from '@mui/icons-material/Edit'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { skyBlue, whiteColor } from 'utils/constants/ColorTheme'

const STEP_PARAMETER = 0
const STEP_THRESHOLD = 1

const TABLE_ALL_HEADERS = ['code', 'name', 'threshold']
const TABLE_NO_CODE_HEADERS = ['name', 'threshold']

const PARAMETERS_ALL_HEADERS = ['code', 'name']
const PARAMETERS_NO_CODE_HEADERS = ['name']

const THRESHOLD_ALL_HEADERS = ['code', 'parameter', 'threshold', 'nullValue']
const THRESHOLD_NO_CODE_HEADERS = ['parameter', 'threshold', 'nullValue']

const Indicator = ({
    isEditMode = false,
    indicator = {},

    onUpdate = () => {},
    onUpdateIndicator = () => {},
    onDeleteIndicator = () => {},
}) => {
    const {
        qualitometers,
        thresholds,
        parameters,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        thresholds: store.QualityReducer.thresholds,
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const nbStations = qualitometers.filter(q => q.stationType === `${indicator.stationType}`).length

    const formattedIndicators = useMemo(() => {
        if (isUndefined(indicator.indicators)) {
            return []
        }
        return indicator.indicators.map(i => {
            return {
                type: indicator.stationType,
                code: i.id,
                threshold: getLabel(thresholds, i.threshold),
                name: getLabel(parameters, i.id),
            }
        })
    }, [indicator, parameters, thresholds])

    const lineActions = [{
        icon: 'close',
        onClick: onDeleteIndicator,
        displayed: isEditMode,
    }, {
        icon: 'edit',
        onClick: onUpdateIndicator,
        displayed: isEditMode,
    }]

    return (
        <Accordion>
            <AccordionSummary color={isEditMode ? mainBlue : greyBlue}>
                <Grid
                    container
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Grid size={5}>
                        <span style={{ fontSize: '1.3rem' }}>
                            {getStationType(indicator.stationType).libelle}
                        </span>
                    </Grid>
                    <Grid size={3}>
                        <span style={{ fontSize: '1.3rem' }}>
                            {`${nbStations} ${i18n.stations}`}
                        </span>
                    </Grid>
                    <Grid size={3}>
                        <span style={{ fontSize: '1.3rem' }}>
                            {`${indicator.indicators?.length ?? 0} ${i18n.indicators}`}
                        </span>
                    </Grid>
                    <Grid size={1}>
                        {
                            isEditMode && (
                                <>
                                    <EditIcon
                                        tooltip={i18n.edit}
                                        onClick={e => {
                                            e.stopPropagation()
                                            onUpdate(indicator.stationType)
                                        }}
                                    />
                                </>
                            )
                        }
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
                <NewTable
                    rows={formattedIndicators}
                    headers={showSandreCode ? TABLE_ALL_HEADERS : TABLE_NO_CODE_HEADERS}
                    rowsPerPageOptions={nbPerPageLabelMedium}
                    lineActions={lineActions}
                />
            </AccordionDetails>
        </Accordion>
    )
}

Indicator.propTypes = {
    isEditMode: PropTypes.bool,
    indicator: PropTypes.shape({
        stationType: PropTypes.number,
        indicators: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            threshold: PropTypes.number,
        })),
    }),
    onUpdate: PropTypes.func,
    onUpdateIndicator: PropTypes.func,
    onDeleteIndicator: PropTypes.func,
}

const SelectParameters = ({
    indicator = {},
    setIndicator = () => { },
}) => {
    const {
        parameters,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const parametersFormatted = useMemo(() => {
        return parameters.map(p => ({
            code: p.code,
            name: p.name.length > 60 && p.shortLabel || p.name || '',
            labelSearch: normalizeString(`${p.code}${p.name ?? ''}${p.shortLabel ?? ''}`),
            id: p.code,
        }))
    }, [parameters])

    const selectedParameters = indicator.indicators?.map(i => `${i.id}`) ?? []

    const onChange = listParam => {
        setIndicator(indi => {
            const indicatorsIndexed = keyBy(indi.indicators, 'id')
            const newIndicators = listParam.map(code => {
                const indiParam = indicatorsIndexed[code]
                if (isUndefined(indiParam)) {
                    return { id: parseInt(code) }
                }
                return indiParam
            })
            return {
                ...indi,
                indicators: newIndicators,
            }
        })
    }

    return (
        <SimpleSelectionTable
            onChange={onChange}

            listData={parametersFormatted}
            selectedList={selectedParameters}

            listHeaders={showSandreCode ? PARAMETERS_ALL_HEADERS : PARAMETERS_NO_CODE_HEADERS}
            listTitle={i18n.nonSelectedParameters}
            selectedListTitle={i18n.selectedParameters}
            maxHeightTable={'43vh'}

            filterField={ParameterFilterField}
            filterFunction={(list, { selection = '-1', listParam, searchValue }) => {
                const filterSelection = selection !== '-1' ? intersectionWith(list, listParam, (elem, id) => elem.id === id) : list
                const searchValueFormat = normalizeString(searchValue)
                return searchValue ? filterSelection.filter(p => p.labelSearch.includes(searchValueFormat)) : filterSelection
            }}
        />
    )
}

SelectParameters.propTypes = {
    indicator: PropTypes.shape({
        stationType: PropTypes.number,
        indicators: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            threshold: PropTypes.number,
        })),
    }),
    setIndicator: PropTypes.func,
}

const SelectThresholds = ({
    indicator = {},
    setIndicator = () => { },
}) => {
    const {
        parameters,
        thresholds,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        thresholds: store.QualityReducer.thresholds,
    }), shallowEqual)

    const indexedParameters = useListIndexed(parameters, 'code')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [selectedIndicators, setSelectedIndicators] = useState({}) // key: parameter code, value: boolean
    const [filter, setFilter] = useState({})
    const [threshold, setThreshold] = useState()

    const isSomeThresholdsSelected = keys(selectedIndicators).some(key => selectedIndicators[key])

    const formattedThresholds = useMemo(() => {
        if (isUndefined(indicator.indicators)) {
            return []
        }
        return indicator.indicators.map(i => {
            const isSelected = selectedIndicators[i.id]
            const parameter = indexedParameters[i.id]

            return {
                code: i.id,
                parameter: showSandreCode ? parameter?.displayLabel : parameter?.displayName,
                threshold: getLabel(thresholds, i.threshold),
                nullValue: isSelected ? <CheckBoxIcon/> : <CheckBoxOutlineBlankIcon/>,
                color: isSelected ? skyBlue : whiteColor,
                searchValue: [parameter?.name, parameter?.shortLabel, parameter?.code].map((label = '') => normalizeString(label)),
            }
        })
    }, [indexedParameters, indicator.indicators, selectedIndicators, thresholds, showSandreCode])


    const filteredThresholds = useMemo(() => {
        const { selection = '-1', listParam, searchValue } = filter
        const filterSelection = selection !== '-1' ? intersectionWith(formattedThresholds, listParam, (a, code) => a.code === code) : formattedThresholds
        const searchValueFormat = normalizeString(searchValue)
        return searchValue ? filterSelection.filter(p => p.searchValue.some(v => v.includes(searchValueFormat))) : filterSelection
    }, [filter, formattedThresholds])

    const isAllThresholdSelected = filteredThresholds.every(th => selectedIndicators[th.code])

    const onClick = () => {
        setSelectedIndicators(prevSelected => {
            return filteredThresholds.reduce((acc, th) => {
                acc[th.code] = !isAllThresholdSelected
                return acc
            }, { ...prevSelected })
        })
    }

    const actions = filteredThresholds.length ? [{
        icon: isAllThresholdSelected ? 'check_box' : 'check_box_outline_blank',
        tooltip: i18n.edit,
        onClick,
    }] : []

    return (
        <Stack container justifyContent='center' rowSpacing={1}>
            <StyledFieldSet>
                <StyledLegend>{i18n.filter}</StyledLegend>
                <ParameterFilterField
                    filter={filter}
                    setFilter={setFilter}
                />
            </StyledFieldSet>
            <CardTable
                title={i18n.thresholds}
                headers={showSandreCode ? THRESHOLD_ALL_HEADERS : THRESHOLD_NO_CODE_HEADERS}
                actions={actions}
                rows={filteredThresholds}
                onClickRow={({ code }) => setSelectedIndicators(prevSelected => ({ ...prevSelected, [code]: !prevSelected[code] }))}
                rowsPerPageOptions={nbPerPageLabel}
            />
            {
                isSomeThresholdsSelected && (
                    <StyledFieldSet>
                        <Grid container alignItems='center'>
                            <Grid size={6}>
                                <Select
                                    options={thresholds}
                                    label={i18n.threshold}
                                    nullLabel='&nbsp;'
                                    onChange={setThreshold}
                                    value={threshold}
                                />
                            </Grid>
                            <Grid size={3} />
                            <Grid size={3}>
                                <Button
                                    onClick={() => {
                                        const thCode = threshold && parseInt(threshold)
                                        setIndicator(prevIndicators => ({
                                            ...prevIndicators,
                                            indicators: prevIndicators.indicators.map(i => {
                                                if (selectedIndicators[i.id]) {
                                                    return { id: i.id, threshold: thCode }
                                                }
                                                return i
                                            }),
                                        }))
                                        setThreshold()
                                        setSelectedIndicators({})
                                    }}
                                    variant='contained'
                                    color='primary'
                                    fullWidth
                                >
                                    {i18n.validate}
                                </Button>
                            </Grid>
                        </Grid>
                    </StyledFieldSet>
                )
            }
        </Stack>
    )
}

SelectThresholds.propTypes = {
    indicator: PropTypes.shape({
        stationType: PropTypes.number,
        indicators: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            threshold: PropTypes.number,
        })),
    }),
    setIndicator: PropTypes.func,
}

const StepperIndicator = ({
    isOpen = false,
    onClose = () => { },
    onSave = () => { },

    selectedIndicator = {},
}) => {
    const [indicator, setIndicator] = useState({})

    useEffect(() => {
        if (isOpen) {
            setIndicator(selectedIndicator)
        }
    }, [isOpen])

    const isThresholdComplete = useMemo(() => {
        if (isUndefined(indicator?.indicators)) {
            return false
        }
        return indicator?.indicators.some(t => isUndefined(t.threshold))
    }, [indicator])

    return (
        <StepperDialog
            steps={[
                {
                    label: i18n.parameters,
                    constant: STEP_PARAMETER,
                },
                {
                    label: i18n.thresholds,
                    constant: STEP_THRESHOLD,
                },
            ]}
            open={isOpen}
            title={i18n.updateIndicator}
            closeDialog={onClose}
            renderSaveButton={step => (step === STEP_THRESHOLD) && (
                <ButtonMUI
                    onClick={() => {
                        onSave(indicator)
                    }}
                    variant='contained'
                    disabled={isThresholdComplete}
                >
                    {i18n.register}
                </ButtonMUI>
            )}
            contentStyle={{ padding: '0 12' }}
        >
            {
                step => (
                    <>
                        {step === STEP_PARAMETER && (
                            <SelectParameters
                                indicator={indicator}
                                setIndicator={setIndicator}
                            />
                        )}
                        {step === STEP_THRESHOLD && (
                            <SelectThresholds
                                indicator={indicator}
                                setIndicator={setIndicator}
                            />
                        )}
                    </>
                )
            }
        </StepperDialog>
    )
}

StepperIndicator.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    selectedIndicator: PropTypes.shape({
        stationType: PropTypes.number,
        indicators: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            threshold: PropTypes.number,
        })),
    }),
}

const PopinIndicator = ({
    isOpen = false,
    indicator = {},
    validate = () => { },
    close = () => { },
}) => {
    const {
        thresholds,
        parameters,
    } = useSelector(store => ({
        thresholds: store.QualityReducer.thresholds,
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [threshold, setThreshold] = useState()

    useEffect(() => {
        if (isOpen) {
            setThreshold(indicator.threshold)
        }
    }, [isOpen, indicator.threshold])

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isOpen}
            PaperProps={{
                sx: {
                    minHeight: undefined,
                    maxHeight: undefined,
                },
            }}
        >
            <DefaultDialogTitle title={i18n.indicator} onClose={close} />
            <DialogContent style={{ overflowX: 'hidden' }}>
                <StyledFieldSet>
                    <Grid
                        container
                        sx={{
                            columnSpacing: 1,
                            rowSpacing: 1,
                            alignItems: 'center',
                            paddingTop: '5px',
                        }}
                    >
                        <Grid size={6}>
                            <Select
                                label={i18n.parameter}
                                value={indicator.id}
                                options={parameters}
                                disabled
                                keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Select
                                label={i18n.threshold}
                                options={thresholds}
                                value={threshold}
                                onChange={setThreshold}
                            />
                        </Grid>
                    </Grid>
                </StyledFieldSet>
            </DialogContent>
            <DialogActions>
                <Grid
                    container
                    alignItems='center'
                    justifyContent='right'
                    columnSpacing={2}
                    sx={{ padding: '0 10' }}
                >
                    <Grid
                        container
                        size={3}
                        justifyContent='flex-end'
                    >
                        <Button
                            onClick={() => {
                                validate({
                                    id: indicator.id,
                                    threshold: parseInt(threshold),
                                })
                            }}
                            variant='contained'
                            color='primary'
                            disabled={isUndefined(threshold)}
                        >
                            {i18n.validate}
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    )
}

PopinIndicator.propTypes = {
    isOpen: PropTypes.bool,
    close: PropTypes.func,
    validate: PropTypes.func,
    indicator: PropTypes.shape({
        id: PropTypes.number,
        threshold: PropTypes.number,
    }),
}

export {
    Indicator,
    StepperIndicator,
    PopinIndicator,
}